import { useCallback, useEffect, useReducer, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { labsKeys, useSubmitNativeLabOrderSetMutation } from '@/entities/labs';

import {
  nativeLabOrderAttemptRegistry,
  type NativeLabOrderAttemptRecord,
  type NativeLabOrderSubmissionData,
} from './nativeLabOrderAttemptRegistry';

interface SelectedAttemptRecord {
  attemptKey?: string;
  record: NativeLabOrderAttemptRecord;
  userId: string;
}

export function useNativeLabOrderSubmission(userId: string, attemptKey?: string) {
  const queryClient = useQueryClient();
  const mutation = useSubmitNativeLabOrderSetMutation();
  const selectedRef = useRef<SelectedAttemptRecord | null>(null);
  const joinedRecordRef = useRef<NativeLabOrderAttemptRecord | null>(null);
  const mountedRef = useRef(false);
  const [, rerender] = useReducer((version: number) => version + 1, 0);

  if (
    selectedRef.current === null ||
    selectedRef.current.userId !== userId ||
    selectedRef.current.attemptKey !== attemptKey
  ) {
    selectedRef.current = {
      attemptKey,
      record: nativeLabOrderAttemptRegistry.acquire(userId, attemptKey),
      userId,
    };
  }

  const record = selectedRef.current.record;
  const outcome = useSyncExternalStore(record.subscribe, record.getSnapshot, record.getSnapshot);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectReplacement = useCallback(() => {
    const selected = selectedRef.current;
    if (!selected) throw new Error('Native lab order attempt is not initialized.');
    const replacement = nativeLabOrderAttemptRegistry.replace(selected.record);
    selectedRef.current = { ...selected, record: replacement };
    if (joinedRecordRef.current === selected.record) joinedRecordRef.current = null;
    rerender();
    return replacement;
  }, []);

  const startNewOrder = useCallback(() => {
    const selected = selectedRef.current;
    if (!selected || selected.record.isPending || selected.record.retryData !== undefined) return;
    selectReplacement();
  }, [selectReplacement]);

  const submit = useCallback(
    async ({ data }: { data: NativeLabOrderSubmissionData }) => {
      let activeRecord = selectedRef.current?.record;
      if (!activeRecord) throw new Error('Native lab order attempt is not initialized.');
      if (activeRecord.isEditableFailure) activeRecord = selectReplacement();

      joinedRecordRef.current = activeRecord;
      rerender();
      try {
        const result = await activeRecord.submit(data, async (attempt) => {
          const response = await mutation.mutateAsync({
            userId: attempt.userId,
            data: { ...attempt.data, orderAttemptId: attempt.orderAttemptId },
          });
          if (response.type === 'success') {
            void queryClient.invalidateQueries({
              queryKey: labsKeys.patientOrders(attempt.userId),
            });
          }
          return response;
        });

        if (result) {
          if (mountedRef.current) nativeLabOrderAttemptRegistry.release(activeRecord);
        }
        return {
          result,
          requiresRecovery: result === null && activeRecord.retryData !== undefined,
        };
      } finally {
        if (joinedRecordRef.current === activeRecord) {
          joinedRecordRef.current = null;
          if (mountedRef.current) rerender();
        }
      }
    },
    [mutation, queryClient, selectReplacement]
  );

  const isJoinedByCurrentHook = joinedRecordRef.current === record && outcome.status === 'pending';
  const state = record.toState(isJoinedByCurrentHook);
  const retryData = record.retryData;

  return {
    state,
    isSubmitting: state.status === 'submitting',
    isDraftLocked:
      state.status === 'submitting' ||
      state.status === 'recovering' ||
      state.status === 'success' ||
      retryData !== undefined,
    retryData,
    startNewOrder,
    submit,
  };
}
